import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Label } from '@/components/ui/label';
import { ExternalLink, Gamepad2, Users, Trophy, Clock, BadgeCheck, Lock, Unlock, X } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { toast } from 'sonner';
import { getDetailedSteamInfo } from '@/services/steamService';

interface SteamProfile {
  steamid: string;
  personaname: string;
  profileurl: string;
  avatar: string;
  avatarmedium: string;
  avatarfull: string;
  personastate: number;
  communityvisibilitystate: number;
  profilestate: number;
  lastlogoff: number;
  commentpermission: number;
  realname?: string;
  primaryclanid?: string;
  timecreated?: number;
  gameid?: string;
  gameserverip?: string;
  gameextrainfo?: string;
  loccountrycode?: string;
  locstatecode?: string;
  loccityid?: string;
}

interface SteamGame {
  appid: number;
  name: string;
  playtime_forever: number;
  img_icon_url: string;
  has_community_visible_stats: boolean;
}

interface SteamGamesResponse {
  game_count: number;
  games: SteamGame[];
}

export function SteamConnection() {
  const { user } = useAuth();
  const [isConnecting, setIsConnecting] = useState(false);
  const [steamProfile, setSteamProfile] = useState<SteamProfile | null>(null);
  const [steamGames, setSteamGames] = useState<SteamGame[]>([]);
  const [steamLevel, setSteamLevel] = useState<number | null>(null);
  const [isPublic, setIsPublic] = useState<boolean | null>(null);
  const [totalPlaytime, setTotalPlaytime] = useState<number | null>(null);
  const [gameCount, setGameCount] = useState<number | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    if (user?.id) {
      loadSteamData();
      handleSteamReturn();
    }
  }, [user?.id]);

  const handleSteamReturn = async () => {
    const urlParams = new URLSearchParams(window.location.search);
    const steamConnected = urlParams.get('steam_connected');

    if (steamConnected === 'true' && user) {
      try {
        const steamId = urlParams.get('openid.identity');
        if (steamId) {
          const steamIdMatch = steamId.match(/\/openid\/id\/(\d+)$/);
          if (steamIdMatch) {
            const extractedSteamId = steamIdMatch[1];

            const { error } = await supabase
              .from('profiles')
              .update({
                steam_id: extractedSteamId,
                steam_url: `https://steamcommunity.com/profiles/${extractedSteamId}`
              })
              .eq('id', user.id);

            if (error) throw error;

            toast.success('Steam account connected successfully!');
            await fetchSteamProfile(extractedSteamId);
            await fetchSteamGames(extractedSteamId);
            window.history.replaceState({}, document.title, window.location.pathname);
          }
        }
      } catch (error) {
        console.error('Error processing Steam return:', error);
        toast.error('Failed to process Steam connection');
      }
    }
  };

  const loadSteamData = async () => {
    if (!user) return;
    try {
      const { data: profile } = await supabase
        .from('profiles')
        .select('steam_id, steam_url')
        .eq('id', user.id)
        .single();

      if (profile?.steam_id) {
        await fetchSteamProfile(profile.steam_id);
        await fetchSteamGames(profile.steam_id);
      }
    } catch (error) {
      console.error('Error loading Steam data:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchSteamProfile = async (steamId: string) => {
    try {
      const data = await getDetailedSteamInfo(steamId);
      if (data) {
        setSteamProfile(data.summary as any);
        setSteamLevel(data.level);
        setIsPublic(data.isPublic);
        setTotalPlaytime(data.totalPlaytimeHours);
        setGameCount(data.gameCount);
      }
    } catch (error) {
      console.error('Error fetching Steam profile:', error);
      toast.error('Failed to load Steam profile');
    }
  };

  const fetchSteamGames = async (steamId: string) => {
    try {
      const { data, error } = await supabase.functions.invoke('steam-proxy', {
        body: { action: 'getOwnedGames', steamId }
      });

      if (error) throw error;

      const responseData: SteamGamesResponse = data.response || data;

      if (responseData.games) {
        const sortedGames = responseData.games
          .sort((a, b) => b.playtime_forever - a.playtime_forever)
          .slice(0, 10);
        setSteamGames(sortedGames);
      }
    } catch (error) {
      console.error('Error fetching Steam games:', error);
      toast.error('Failed to load Steam games');
    }
  };

  const handleSteamConnect = async () => {
    if (!user) return;
    setIsConnecting(true);
    try {
      const returnUrl = `${window.location.origin}/settings?steam_connected=true`;
      const steamOpenIdUrl = `https://steamcommunity.com/openid/login?openid.ns=http://specs.openid.net/auth/2.0&openid.mode=checkid_setup&openid.return_to=${encodeURIComponent(returnUrl)}&openid.realm=${encodeURIComponent(window.location.origin)}&openid.identity=http://specs.openid.net/auth/2.0/identifier_select&openid.claimed_id=http://specs.openid.net/auth/2.0/identifier_select`;
      window.location.href = steamOpenIdUrl;
    } catch (error) {
      console.error('Error connecting Steam:', error);
      toast.error('Failed to connect Steam account');
      setIsConnecting(false);
    }
  };

  const handleSteamDisconnect = async () => {
    if (!user) return;
    try {
      const { error } = await supabase
        .from('profiles')
        .update({
          steam_id: null,
          steam_url: null
        })
        .eq('id', user.id);

      if (error) throw error;
      setSteamProfile(null);
      setSteamGames([]);
      setSteamLevel(null);
      setIsPublic(null);
      toast.success('Steam account disconnected');
    } catch (error) {
      console.error('Error disconnecting Steam:', error);
      toast.error('Failed to disconnect Steam account');
    }
  };

  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Gamepad2 className="w-5 h-5" />
            Steam Connection
          </CardTitle>
          <CardDescription>
            Connect your Steam account to sync your gaming data
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="animate-pulse space-y-4">
            <div className="h-4 bg-muted rounded w-1/2"></div>
            <div className="h-10 bg-muted rounded"></div>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <div>
      <Label htmlFor="steam" className="flex items-center gap-2 mb-1">
        <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
          <path d="M11.979 0C5.678 0 .511 4.86.022 11.037l6.432 2.658c.545-.371 1.203-.59 1.912-.59.063 0 .125.004.188.006l2.861-4.142V8.91c0-2.495 2.028-4.524 4.524-4.524 2.494 0 4.524 2.031 4.524 4.527s-2.03 4.525-4.524 4.525h-.105l-4.076 2.911c0 .052.004.105.004.159 0 1.875-1.515 3.396-3.39 3.396-1.635 0-3.016-1.173-3.331-2.727L.436 15.27C1.862 20.307 6.486 24 11.979 24c6.627 0 11.999-5.373 11.999-12S18.605 0 11.979 0zM7.54 18.21l-1.473-.61c.262.543.714.999 1.314 1.25 1.297.539 2.793-.076 3.332-1.375.263-.63.264-1.319.005-1.949s-.75-1.121-1.377-1.383c-.624-.26-1.29-.249-1.878-.03l1.523.63c.956.4 1.409 1.5 1.009 2.455-.397.957-1.497 1.41-2.454 1.012H7.54zm11.415-9.303c0-1.662-1.353-3.015-3.015-3.015-1.665 0-3.015 1.353-3.015 3.015 0 1.665 1.35 3.015 3.015 3.015 1.663 0 3.015-1.35 3.015-3.015zm-5.273-.005c0-1.252 1.013-2.266 2.265-2.266 1.249 0 2.266 1.014 2.266 2.266 0 1.251-1.017 2.265-2.266 2.265-1.253 0-2.265-1.014-2.265-2.265z" />
        </svg>
        Steam
      </Label>
      {steamProfile ? (
        <div className="space-y-3">
          <div className="flex items-center gap-3 p-3 bg-muted/50 rounded-custom-paddings">
            <button
              onClick={() => window.open(steamProfile.profileurl, '_blank')}
              className="flex items-center gap-3 flex-1 hover:opacity-80 transition-opacity"
            >
              <Avatar className="w-8 h-8">
                <AvatarImage src={steamProfile.avatarfull} alt={steamProfile.personaname} />
                <AvatarFallback>
                  <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M11.979 0C5.678 0 .511 4.86.022 11.037l6.432 2.658c.545-.371 1.203-.59 1.912-.59.063 0 .125.004.188.006l2.861-4.142V8.91c0-2.495 2.028-4.524 4.524-4.524 2.494 0 4.524 2.031 4.524 4.527s-2.03 4.525-4.524 4.525h-.105l-4.076 2.911c0 .052.004.105.004.159 0 1.875-1.515 3.396-3.39 3.396-1.635 0-3.016-1.173-3.331-2.727L.436 15.27C1.862 20.307 6.486 24 11.979 24c6.627 0 11.999-5.373 11.999-12S18.605 0 11.979 0zM7.54 18.21l-1.473-.61c.262.543.714.999 1.314 1.25 1.297.539 2.793-.076 3.332-1.375.263-.63.264-1.319.005-1.949s-.75-1.121-1.377-1.383c-.624-.26-1.29-.249-1.878-.03l1.523.63c.956.4 1.409 1.5 1.009 2.455-.397.957-1.497 1.41-2.454 1.012H7.54zm11.415-9.303c0-1.662-1.353-3.015-3.015-3.015-1.665 0-3.015 1.353-3.015 3.015 0 1.665 1.35 3.015 3.015 3.015 1.663 0 3.015-1.35 3.015-3.015zm-5.273-.005c0-1.252 1.013-2.266 2.265-2.266 1.249 0 2.266 1.014 2.266 2.266 0 1.251-1.017 2.265-2.266 2.265-1.253 0-2.265-1.014-2.265-2.265z" />
                  </svg>
                </AvatarFallback>
              </Avatar>
              <div className="flex-1 text-left">
                <p className="font-medium text-sm">{steamProfile.personaname}</p>
                <div className="flex items-center gap-2 mt-0.5">
                  <div className="flex items-center gap-1 text-[10px] text-muted-foreground">
                    {isPublic ? (
                      <>
                        <Unlock className="w-2.5 h-2.5" />
                        <span>Public</span>
                      </>
                    ) : (
                      <>
                        <Lock className="w-2.5 h-2.5" />
                        <span>Private</span>
                      </>
                    )}
                  </div>
                  <Badge variant="outline" className="whitespace-nowrap h-4 text-[10px] px-1 font-normal border-blue-500/30 text-blue-500">
                    Level {steamLevel ?? '?'}
                  </Badge>
                  {gameCount !== null && (
                    <div className="text-[10px] text-muted-foreground whitespace-nowrap">
                      {gameCount} games
                    </div>
                  )}
                </div>
              </div>
            </button>
            <Button
              variant="destructive"
              size="sm"
              onClick={handleSteamDisconnect}
              className="h-8 px-2"
            >
              <X className="w-4 h-4" />
            </Button>
          </div>
        </div>
      ) : (
        <div className="space-y-2">
          <p className="text-sm text-muted-foreground">
            Connect your Steam account to sync your gaming data
          </p>
          <Button
            onClick={handleSteamConnect}
            disabled={isConnecting}
            variant="outline"
            className="w-full"
          >
            {isConnecting ? 'Connecting...' : 'Connect with Steam'}
          </Button>
        </div>
      )}
    </div>
  );
}
